<template>
  <div :class="wrapperClasses">
    <a-visibility
      v-if="article.sticker"
      show
      :on="[$breakpoint.tablet, $breakpoint.desktopSm]"
    >
      <a-sticker :sticker="article.sticker" class="news-item__sticker" />
    </a-visibility>

    <slot name="top" />

    <div class="news-item__item-container">
      <additional-info
        v-if="!hideAdditionalInfo"
        class="news-item__block_left"
        :category="category"
        :tag="article.mainTag"
        :show-tag="showTag"
        :sticker="article.sticker"
        :isPinned="article.isPinned"
      />
      <main-info
        :article="article"
        :title-html-tag="titleHtmlTag"
        class="news-item__block_middle"
      />
      <a-visibility show :from="$breakpoint.desktopMd">
        <div
          ref="iconContainer"
          class="news-item__block_right"
          :style="iconListStyle"
        >
          <template v-if="!isIconListEmpty">
            <a-action-icon
              v-for="item in iconList"
              :key="item.icon"
              :action-icon="item.actionIcon"
              :titleWidth="titleWidth"
              height-auto
              v-bind="item.props"
              :aria-label="item.ariaLabel"
              v-on="$listeners"
            />
          </template>
        </div>
      </a-visibility>
    </div>

    <slot name="bottom" />
  </div>
</template>

<script>
import mixins from '@/utils/mixins'
import AdditionalInfo from './AdditionalInfo'
import MainInfo from './MainInfo'
import AActionIcon, { ACTION_ICON } from 'shared/AActionIcon'
import { propValidator, PROP_TYPES } from '@/utils/validators'
import ASticker from 'shared/ASticker/index'
import { serverCacheKey } from '@/utils/mixins/serverCacheKey'
import { HTML_TAG } from '@/utils/helpers/processHtml/enums'

export { ACTION_ICON }

const ACTION_ICON_LIST = Object.values(ACTION_ICON)

export const ARTICLE_EMAIL_SHARING_TEXT = {
  TITLE: topic => `You're going to love it: ${topic}`,
  DESCRIPTION: url => `Check out this article from Finance Magnates: ${url}`
}

const ICON_WIDTH = 35

export default {
  name: 'ANewsItem',
  mixins: [mixins.refDimensions, serverCacheKey],
  components: { ASticker, AdditionalInfo, MainInfo, AActionIcon },
  props: {
    article: propValidator([PROP_TYPES.OBJECT]),
    actionIcons: propValidator(
      [PROP_TYPES.ARRAY],
      false,
      () => [
        ACTION_ICON.SAVE,
        ACTION_ICON.FOLLOW,
        ACTION_ICON.LEARN_ABOUT,
        ACTION_ICON.SHARE
      ],
      arr => arr.every(item => ACTION_ICON_LIST.includes(item))
    ),
    hideBottomBorder: propValidator([PROP_TYPES.BOOLEAN], false, false),
    iconMaxSpace: propValidator([PROP_TYPES.NUMBER], false, 30),
    iconMinSpace: propValidator([PROP_TYPES.NUMBER], false, 15),
    hideAdditionalInfo: propValidator([PROP_TYPES.BOOLEAN], false, false),
    showTag: propValidator([PROP_TYPES.BOOLEAN], false, false),
    titleHtmlTag: propValidator([PROP_TYPES.STRING], false, HTML_TAG.HEADING_H3)
  },
  computed: {
    iconList() {
      return this.actionIcons.map(name => this.availableIconList[name])
    },
    availableIconList() {
      return {
        [ACTION_ICON.SAVE]: {
          actionIcon: ACTION_ICON.SAVE,
          props: {
            id: this.article.id,
            isSaved: this.article.isSaved
          },
          ariaLabel: 'Save'
        },
        [ACTION_ICON.FOLLOW]: {
          actionIcon: ACTION_ICON.FOLLOW,
          props: {
            tag: this.article.mainTag
          },
          ariaLabel: 'Follow'
        },
        [ACTION_ICON.LEARN_ABOUT]: {
          actionIcon: ACTION_ICON.LEARN_ABOUT,
          props: {
            term: this.article.mainTerm
          },
          ariaLabel: 'Learn About'
        },
        [ACTION_ICON.SHARE]: {
          actionIcon: ACTION_ICON.SHARE,
          props: {
            title: this.article.title,
            path: this.article.canonicalPath,
            description: this.$helper.parseTldrJSONToText(this.article.tldr),
            emailTitle: ARTICLE_EMAIL_SHARING_TEXT.TITLE(this.article.title),
            emailDescription: ARTICLE_EMAIL_SHARING_TEXT.DESCRIPTION(
              this.article.canonicalUrl
            )
          },
          ariaLabel: 'Share'
        }
      }
    },
    wrapperClasses() {
      return {
        'news-item__wrapper': true,
        'news-item__wrapper_border-bottom': !this.hideBottomBorder
      }
    },
    isIconListEmpty() {
      return !this.iconList.length
    },
    iconListStyle() {
      const flexBasis =
        this.iconList.length * (ICON_WIDTH + this.iconMaxSpace) -
        this.iconMaxSpace
      const minWidth =
        this.iconList.length * (ICON_WIDTH + this.iconMinSpace) -
        this.iconMinSpace
      return {
        flexBasis: `${flexBasis}px`,
        minWidth: `${minWidth}px`
      }
    },
    category() {
      const {
        categoryName,
        categorySlug,
        subcategoryName,
        subcategorySlug
      } = this.article

      return {
        categoryName,
        categorySlug,
        subcategoryName,
        subcategorySlug
      }
    },
    titleWidth() {
      return this.refDimensions.iconContainer
        ? this.refDimensions.iconContainer.width / this.iconList.length
        : 90
    }
  },
  mounted() {
    this.$emit('mounted')
  }
}
</script>

<style lang="scss" scoped>
.news-item {
  &__wrapper {
    padding: 20px 0;
    position: relative;
    border-bottom: 2px solid transparent;

    &_border-bottom {
      border-bottom: 2px solid black;
    }
  }

  &__item-container {
    display: flex;

    @include desktop-sm {
      flex-direction: column-reverse;
    }
  }

  &__block {
    &_left {
      flex: 1 0 auto;
      width: 160px;

      @include desktop-sm {
        width: auto;
      }
    }

    &_middle {
      flex: 6 1 900px;
      padding-right: 35px;

      @include desktop-sm {
        flex-basis: auto;
      }
    }
  }

  &__sticker {
    /deep/ .article-sticker__img {
      margin-bottom: 0;
      margin-right: 0;
    }

    @include desktop-sm {
      margin-bottom: 10px;
    }
  }
}

.news-item__item-container .news-item__block_right {
  position: static;
  flex-grow: 2;
  flex-shrink: 1;
  display: flex;
  align-self: flex-start;
  justify-content: space-between;
  align-items: flex-start;
}
</style>
